const API_TOKEN = 'd8570b1f-c3b1-42a4-9b1a-f509e88c30d9'
const BASE_URL = 'https://api.stealthex.io'

export interface StealthexToken {
  id: string
  name: string
  logo: string
  symbol: string
  addressValidation?: string
  standard: string
  hasExtraId: boolean
  extraId?: string
}

interface CurrencyApiResponse {
  symbol: string
  network: string
  has_extra_id: boolean
  extra_id: string
  name: string
  validation_address: string
  validation_extra: string
  address_explorer: string
  tx_explorer: string
  image: string
}

interface EstimatedAmountApiResponse {
  estimated_amount: string
}

interface RangeApiResponse {
  min_amount: string
  max_amount?: string
}

interface ExchangeApiObject {
  id: string
  type: string
  timestamp: string
  updated_at: string
  currency_from: string
  currency_to: string
  amount_from: string
  expected_amount: string
  amount_to: string
  address_from: string
  address_to: string
  extra_id_from: string
  extra_id_to: string
  tx_from: string
  tx_to: string
  status: string
  currencies: { [key: string]: CurrencyApiResponse }
  refund_address: string
  refund_extra_id: string
}

export interface ExchangeApiResponse {
  data: ExchangeApiObject
  signature: string
}

export interface Range {
  minAmount: number
  maxAmount?: number
}

interface NewExchangeProps {
  currencyFrom: string
  currencyTo: string
  addressTo: string
  amountFrom: number
}

class Stealthex {
  private async request<T>(url: string, params: URLSearchParams = new URLSearchParams()) {
    params.append('api_key', API_TOKEN)

    const result = await fetch(`${BASE_URL}/${url}?${params.toString()}`)
    return result.json() as Promise<T>
  }

  private async post<T>(url: string, body: object) {
    const result = await fetch(`${BASE_URL}/${url}?api_key=${API_TOKEN}`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json' },
    })
    return result.json() as Promise<T>
  }

  async getAvailableTokens() {
    const response: CurrencyApiResponse[] = await this.request('api/v2/currency')
    return response.map(
      ({ name, image, symbol, validation_address, has_extra_id, extra_id, network }) => ({
        id: symbol,
        name,
        symbol: symbol.toUpperCase(),
        logo: image,
        standard: network,
        addressValidation: validation_address,
        hasExtraId: has_extra_id,
        extraId: extra_id,
      })
    )
  }

  getPairs(from: string): Promise<string[]> {
    return this.request(`api/v2/pairs/${from}`)
  }

  async getEstimatedAmount(from: string, to: string, amount: number) {
    const params = new URLSearchParams()
    params.append('fixed', 'false')
    params.append('amount', `${amount}`)

    const response: EstimatedAmountApiResponse = await this.request(
      `api/v2/estimate/${from}/${to}`,
      params
    )

    return parseFloat(response.estimated_amount)
  }

  async getRange(from: string, to: string): Promise<Range> {
    const response: RangeApiResponse = await this.request(`api/v2/range/${from}/${to}`)

    return {
      minAmount: parseFloat(response.min_amount),
      maxAmount: response.max_amount ? parseFloat(response.max_amount) : undefined,
    }
  }

  async createExchange({
    currencyFrom,
    currencyTo,
    addressTo,
    amountFrom,
  }: NewExchangeProps): Promise<ExchangeApiResponse> {
    const body = {
      currency_from: currencyFrom,
      currency_to: currencyTo,
      address_to: addressTo,
      amount_from: amountFrom,
    }

    const reponse: ExchangeApiResponse = await this.post(`api/v3/exchange`, body)
    return reponse
  }

  getExchangeInfo(id: string): Promise<ExchangeApiResponse> {
    return this.request(`api/v3/exchange/${id}`)
  }
}

export default Stealthex
